import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({ title, description, keywords, image, schema }) => {
  const location = useLocation();
  const baseUrl = 'https://bgtechnochem.com';
  const currentUrl = `${baseUrl}${location.pathname}`;

  const defaultTitle = "Water Treatment Plant Manufacturer in Ludhiana | STP, ETP, RO Plant | BG TECHNO CHEM";
  const defaultDescription = "Leading water treatment plant manufacturer in Ludhiana, Punjab. STP, ETP, WTP, RO Systems & water treatment chemicals. ISO 9001 & 14001 certified with 28+ years of experience.";
  const defaultImage = "https://images.unsplash.com/photo-1533077162801-86490c593afb";
  const defaultKeywords = "water treatment plant, water treatment plant manufacturer, water treatment chemicals, chemical water treatment, STP plant, ETP plant, sewage treatment plant, effluent treatment plant, industrial RO plant, RO antiscalant, boiler water treatment chemicals, cooling tower chemicals, descaling chemicals, water treatment plant in Ludhiana, water treatment chemical supplier Punjab, water treatment company Punjab, water treatment plant price";

  const pageTitle = title ? `${title} | BG TECHNO CHEM` : defaultTitle;

  // LocalBusiness schema rendered on every page for strong local SEO signals
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "BG TECHNO CHEM",
    "description": "Manufacturer and supplier of water treatment plants, STP, ETP, RO systems and water treatment chemicals in Ludhiana, Punjab.",
    "image": defaultImage,
    "logo": `${baseUrl}/logo.svg`,
    "telephone": "+91 98142 04405",
    "email": "bgtechnochem2000@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "S.C.O. 26, 27, 28, Bank Colony Road, Jamalpur Chowk",
      "addressLocality": "Ludhiana",
      "addressRegion": "Punjab",
      "postalCode": "141015",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 30.9010,
      "longitude": 75.8573
    },
    "url": baseUrl,
    "priceRange": "$$",
    "areaServed": ["Ludhiana", "Punjab", "India"],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "sameAs": [
      "https://linkedin.com",
      "https://instagram.com"
    ]
  };

  // Support one or many extra schema objects passed per page
  const extraSchemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description || defaultDescription} />
        <meta name="keywords" content={keywords || defaultKeywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BG TECHNO CHEM" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description || defaultDescription} />
        <meta property="og:image" content={image || defaultImage} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description || defaultDescription} />
        <meta name="twitter:image" content={image || defaultImage} />
      </Helmet>

      {/* Structured Data rendered directly into the DOM (reliably crawlable) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(businessSchema) }}
      />
      {extraSchemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}
    </>
  );
};

export default SEO;
